import React, { useState } from 'react';
import {
  Container,
  ActionsContainer,
  RenewCreditRequestedModal
} from './styles';
import CreditDetailsModalContent from './CreditDetailsModalContent';
import Skeleton from 'react-loading-skeleton';
import {
  formatCurrency,
  formatPercentualNumbers
} from '../../../utils/formatNumbers';
import moment from 'moment';
import CreditRequestedMessageModalContent from './CreditRequestedMessageModalContent';
import { ICreditLimitsState } from '../../../store/ducks/credit-limits/types';
import {
  CustomersState,
  ICustomer
} from '../../../store/ducks/customers/types';
import CreditRenew from './CreditRenew';
import { uppercaseOnlyFirstLetter } from '../../../utils/formatStrings';
import {
  InvoicesState,
  IInvoiceCreditsState
} from '../../../store/ducks/invoices/types';
import { Button, Modal, Title } from '@gerdau/hefesto';
import { KeyValueItem, Tag } from '@gerdau/hefesto-mais-web';
import { SimpleGraph } from '@gerdau/hefesto-mais-web/SimpleGraph';
import { useDispatch } from 'react-redux';
import { sendTagging } from '../../../store/ducks/tagging/actions';

interface IFinancialSubheaderProps {
  selectedCustomer?: ICustomer;
  invoicesState: InvoicesState;
  creditLimitsState: ICreditLimitsState;
  invoicesCreditsState: IInvoiceCreditsState;
  customersState: CustomersState;
  customers: ICustomer[];
}

const SubHeader: React.FC<IFinancialSubheaderProps> = ({
  selectedCustomer,
  invoicesState,
  creditLimitsState,
  invoicesCreditsState,
  customersState
}) => {
  const [showModalCreditsDetail, setShowModalCreditsDetail] = useState(false);

  const dispatch = useDispatch();

  const [
    canShowRenewCreditRequestedModal,
    setCanShowRenewCreditRequestedModal
  ] = useState(false);

  const canShowRenewCreditButton = (): boolean => {
    if (!creditLimitsState.creditLimits) {
      return false;
    }

    const creditLimitValidDate = moment(
      creditLimitsState.creditLimits.creditLimitValidDate,
      'DD-MM-YYYY'
    );
    const expireIn45days = moment()
      .add(45, 'days')
      .isSameOrAfter(creditLimitValidDate);

    return (
      !creditLimitsState.loading &&
      !selectedCustomer?.creditRenew &&
      expireIn45days
    );
  };

  const canShowRenewCreditLimitRequestedMessage = (): boolean => {
    if (!creditLimitsState.creditLimits) {
      return false;
    }

    const creditLimitValidDate = moment(
      creditLimitsState.creditLimits.creditLimitValidDate,
      'DD-MM-YYYY'
    );
    const expireIn45days = moment()
      .add(45, 'days')
      .isSameOrAfter(creditLimitValidDate);

    return (
      expireIn45days &&
      creditLimitsState.loading === false &&
      selectedCustomer?.creditRenew === true
    );
  };

  const handleShowModalCreditsDetails = (): void => {
    dispatch(
      sendTagging({
        screenName: 'LG:Financas:MinhasFinancas',
        event: 'click',
        itemName: 'button:detalhes_credito',
        itemDescription: 'financas_minhas_financas_detalhesCredito'
      })
    );
    setShowModalCreditsDetail(true);
  };

  return (
    <Container>
      {selectedCustomer && (
        <div className="header">
          <Title level={3}>{`${uppercaseOnlyFirstLetter(
            selectedCustomer.name
          )}`}</Title>
          <ActionsContainer>
            {creditLimitsState.error || !creditLimitsState.creditLimits ? (
              ' - '
            ) : (
              <div className="button">
                <Button
                  fullWidth
                  variant="secondary"
                  onClick={handleShowModalCreditsDetails}
                  name={''}
                  loading={creditLimitsState.loading}
                >
                  Detalhes de crédito
                </Button>
              </div>
            )}

            {selectedCustomer &&
              (canShowRenewCreditButton() || selectedCustomer.creditRenew) && (
                <div className={canShowRenewCreditButton() ? 'button' : ''}>
                  <CreditRenew
                    creditLimitsState={creditLimitsState}
                    selectedCustomer={selectedCustomer}
                  />
                </div>
              )}
          </ActionsContainer>
        </div>
      )}

      {selectedCustomer && (
        <div className="content">
          <KeyValueItem title="Saldo">
            {invoicesCreditsState.loading ? (
              <Skeleton width={100} />
            ) : invoicesCreditsState.error ||
              !invoicesCreditsState.creditSum ||
              Number(invoicesCreditsState.creditSum) === 0 ? (
              <span className="value">-</span>
            ) : (
              `R$ ${formatCurrency(
                invoicesCreditsState.creditSum.replace(/,/g, '')
              )}`
            )}
          </KeyValueItem>

          <KeyValueItem title="A vencer">
            {invoicesState.loading ? (
              <Skeleton width={100} />
            ) : invoicesState.error ||
              !invoicesState.invoicesSummary ||
              !invoicesState.invoicesSummary.openInvoiceSum ||
              Number(invoicesState.invoicesSummary.openInvoiceSum) === 0 ? (
              <span className="value">-</span>
            ) : (
              `R$ ${formatCurrency(
                invoicesState.invoicesSummary.openInvoiceSum.replace(/,/g, '')
              )}`
            )}
          </KeyValueItem>

          <KeyValueItem title="Vencidas">
            {invoicesState.loading ? (
              <Skeleton width={100} />
            ) : invoicesState.error ||
              !invoicesState.invoicesSummary ||
              !invoicesState.invoicesSummary.dueInvoiceSum ||
              Number(invoicesState.invoicesSummary.dueInvoiceSum) === 0 ? (
              <span className="value">-</span>
            ) : (
              `R$ ${formatCurrency(
                invoicesState.invoicesSummary.dueInvoiceSum.replace(/,/g, '')
              )}`
            )}
          </KeyValueItem>

          <KeyValueItem title="Limite de crédito ">
            {creditLimitsState.loading ? (
              <Skeleton width={100} />
            ) : creditLimitsState.error || !creditLimitsState.creditLimits ? (
              ' - '
            ) : (
              `R$ ${formatCurrency(creditLimitsState.creditLimits.creditLimit)}`
            )}
          </KeyValueItem>

          <KeyValueItem title="Crédito utilizado ">
            {creditLimitsState.loading ? (
              <Skeleton width={175} />
            ) : creditLimitsState.error || !creditLimitsState.creditLimits ? (
              ' - '
            ) : (
              <div className="simple-graph">
                <SimpleGraph
                  percentage={Number(
                    formatPercentualNumbers(
                      creditLimitsState.creditLimits.total.usedCreditPercentage
                    )
                  )}
                >
                  {`${Number(
                    formatPercentualNumbers(
                      creditLimitsState.creditLimits.total.usedCreditPercentage
                    )
                  )}%`}
                </SimpleGraph>
              </div>
            )}
          </KeyValueItem>

          <div>
            <KeyValueItem title="Vencimento">
              {creditLimitsState.loading ? (
                <Skeleton width={80} />
              ) : creditLimitsState.error || !creditLimitsState.creditLimits ? (
                ' - '
              ) : (
                <span>
                  {creditLimitsState.creditLimits.creditLimitValidDate.replace(
                    /-/g,
                    '/'
                  )}
                </span>
              )}
            </KeyValueItem>

            {canShowRenewCreditLimitRequestedMessage() && (
              <div className="credit-renew">
                <a
                  href="/financials"
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    setCanShowRenewCreditRequestedModal(true);
                  }}
                >
                  <Tag variant={'success'} tooltipTitle="Renovação Solicitada">
                    Renovação solicitada
                  </Tag>
                </a>

                {selectedCustomer && (
                  <RenewCreditRequestedModal
                    width={450}
                    footer={[
                      <Button
                        variant="primary"
                        key="ok"
                        onClick={() =>
                          setCanShowRenewCreditRequestedModal(false)
                        }
                        name={'Entendi'}
                      >
                        Entendi
                      </Button>
                    ]}
                    closable={false}
                    onCancel={() => setCanShowRenewCreditRequestedModal(false)}
                    visible={canShowRenewCreditRequestedModal}
                  >
                    <CreditRequestedMessageModalContent
                      selectedCustomer={selectedCustomer}
                      customersState={customersState}
                    />
                  </RenewCreditRequestedModal>
                )}
              </div>
            )}
          </div>

          {showModalCreditsDetail && (
            <Modal
              title="Seu crédito"
              onClose={() => setShowModalCreditsDetail(false)}
              modalSize="lg"
              buttons={{
                right: {
                  children: 'Fechar',
                  name: 'fechar',
                  onClick: () => setShowModalCreditsDetail(false),
                  type: 'button'
                }
              }}
            >
              {creditLimitsState.creditLimits && (
                <CreditDetailsModalContent
                  creditLimit={creditLimitsState.creditLimits}
                />
              )}
            </Modal>
          )}
        </div>
      )}
    </Container>
  );
};

export default SubHeader;
